import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Phone, MessageCircle, ArrowRight, ArrowLeft, ShieldCheck, HeartHandshake,
  Clock, Gavel, Layers, Mail, Download, Loader2, LifeBuoy, CheckCircle2, Info,
} from "lucide-react";
import {
  CLAIM_DESK, CLAIM_DESK_WA_MESSAGE, telHref, waHref, mailHref, buildClaimDeskVCard,
} from "@/config/claimDesk";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BENEFITS = [
  { icon: HeartHandshake, title: "Personal claim handling", desc: "Your advisor coordinates with hospital, TPA and insurer end to end. You don't chase anyone." },
  { icon: Clock, title: "24/7 backup contacts", desc: "If your advisor is unreachable, two backup numbers are always live. Odd hour, weekend, festival — someone picks up." },
  { icon: Gavel, title: "We fight wrongful rejections", desc: "If your claim is valid and gets rejected or short-settled, we escalate: insurer grievance cell → IRDAI Bima Bharosa → Insurance Ombudsman. We prepare the case and the paperwork with you." },
  { icon: Layers, title: "Double layer of support", desc: "For policies bought through Beshak, you also get Beshak CARES as an independent second layer of backup." },
];

const STEPS = [
  "Call or WhatsApp the moment a hospitalisation is planned or happens — before admission if possible.",
  "We handle pre-auth, documents and follow-ups with the hospital/TPA/insurer and keep you updated.",
  "Rejected or delayed? We review the rejection, and if the claim is valid, we escalate all the way to the Ombudsman.",
];

const STRAIGHT_TALK = [
  "We are your advisor and advocate, not the insurer or TPA. The final claim decision rests with the insurer, Ombudsman or courts.",
  "We don't promise approvals. We promise you won't fight alone and your case will be presented properly.",
  "Our dispute support is guidance and documentation, not legal representation in court.",
  "Claim Desk is free for all policies serviced by Kavach Box / Shree Finserv.",
];

const EMPTY_FORM = { name: "", policy_number: "", insurer: "", hospital_name: "", phone: "", description: "" };

const inputCls = "w-full px-3.5 py-3 bg-white border border-[#C9D4E6] rounded-xl text-[#1B2A4A] text-sm placeholder:text-[#5A6880] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/40 focus:border-[#D4A017]";

export default function ClaimDesk() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Claim Desk — Kavach Box | 24/7 Claim Assistance & Dispute Support";
    const desc = "Kavach Box Claim Desk — 24/7 claim assistance, backup contacts and dispute escalation support for your health and life insurance claims.";
    let tag = document.querySelector('meta[name="description"]');
    const hadTag = !!tag;
    const prevDesc = tag ? tag.getAttribute("content") : null;
    if (!tag) { tag = document.createElement("meta"); tag.setAttribute("name", "description"); document.head.appendChild(tag); }
    tag.setAttribute("content", desc);
    return () => {
      document.title = prevTitle;
      if (hadTag && prevDesc !== null) tag.setAttribute("content", prevDesc);
    };
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const downloadVCard = () => {
    const blob = new Blob([buildClaimDeskVCard()], { type: "text/vcard;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "Kavach-Box-Claim-Desk.vcf";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Claim Desk contact saved");
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (form.phone.replace(/\D/g, "").length < 10) return toast.error("Please enter a valid 10-digit phone number");
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/claim-intimation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSubmitted(true);
        setForm(EMPTY_FORM);
        toast.success("Claim intimated — our Claim Desk will call you shortly");
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d.detail || "Something went wrong. Please call us instead.");
      }
    } catch {
      toast.error("Network error. Please call the Claim Desk directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const CallBtn = ({ testid, className = "" }) => (
    <a href={telHref(CLAIM_DESK.primary.phone)} data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 h-12 sm:h-13 px-6 rounded-xl bg-[#D4A017] hover:bg-[#c1930f] text-[#0A0F1A] font-bold text-sm sm:text-base shadow-lg shadow-[#D4A017]/25 active:scale-95 transition-transform ${className}`}>
      <Phone className="h-5 w-5" /> Call Claim Desk 24/7
    </a>
  );
  const WaBtn = ({ testid, className = "" }) => (
    <a href={waHref(CLAIM_DESK.whatsapp, CLAIM_DESK_WA_MESSAGE)} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 h-12 sm:h-13 px-6 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold text-sm sm:text-base shadow-lg shadow-green-600/25 active:scale-95 transition-transform ${className}`}>
      <MessageCircle className="h-5 w-5" /> WhatsApp Claim Desk
    </a>
  );

  return (
    <div className="min-h-screen bg-[#F4F6FA] text-[#1B2A4A]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-[#C9D4E6]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="p-2 -ml-2 rounded-lg hover:bg-[#EDF1F8]" data-testid="claim-desk-back-btn" aria-label="Home">
            <ArrowLeft className="h-5 w-5 text-[#1B2A4A]" />
          </button>
          <button onClick={() => navigate("/")} className="flex items-center" data-testid="claim-desk-logo">
            <img src="/kavachbox-logo-light.png" alt="Kavach Box" className="h-9 w-auto" />
          </button>
          <div className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-[#A87608]">
            <LifeBuoy className="h-4 w-4" /> Claim Desk
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-24">
        {/* HERO */}
        <section className="pt-10 pb-8" data-testid="claim-desk-hero">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-1.5 badge-gold mb-5"><ShieldCheck className="h-4 w-4" /> {CLAIM_DESK.hours} Claim Support</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-4">
              Buying is easy. <span className="text-gradient-gold">Claims are where we prove ourselves.</span>
            </h1>
            <p className="text-lg text-[#2E3B57] max-w-2xl mb-7 leading-relaxed">
              Every Kavach Box policy comes with our Claim Desk — a real team standing behind you when it matters most.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <CallBtn testid="hero-call-btn" className="w-full sm:w-auto" />
              <WaBtn testid="hero-whatsapp-btn" className="w-full sm:w-auto" />
            </div>
          </motion.div>
        </section>

        {/* WHAT YOU GET */}
        <section className="py-8" data-testid="what-you-get-section">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-5">What you get</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="glass-card p-5 sm:p-6" data-testid={`benefit-card-${i}`}>
                  <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center mb-4 shadow-lg shadow-[#D4A017]/15">
                    <Icon className="h-6 w-6 text-[#0D1A3A]" />
                  </div>
                  <h3 className="font-bold text-[#1B2A4A] text-base mb-1.5">{b.title}</h3>
                  <p className="text-[#2E3B57] text-sm leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-8" data-testid="how-it-works-section">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-5">How it works</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {STEPS.map((s, i) => (
              <div key={i} className="glass-card p-5 sm:p-6 relative" data-testid={`how-step-${i + 1}`}>
                <div className="h-10 w-10 rounded-full bg-[#1B2A4A] text-white font-bold flex items-center justify-center mb-4 text-lg">{i + 1}</div>
                <p className="text-[#2E3B57] text-sm leading-relaxed">{s}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CLAIM DESK CONTACTS */}
        <section className="py-8" data-testid="contacts-section">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-5">Claim Desk contacts</h2>
          <div className="glass-card p-5 sm:p-6">
            <div className="space-y-2.5">
              {[{ label: CLAIM_DESK.primary.name, phone: CLAIM_DESK.primary.phone, tag: "Primary", testid: "contact-primary" },
                { label: CLAIM_DESK.backups[0].name, phone: CLAIM_DESK.backups[0].phone, tag: "Backup 1", testid: "contact-backup-1" },
                { label: CLAIM_DESK.backups[1].name, phone: CLAIM_DESK.backups[1].phone, tag: "Backup 2", testid: "contact-backup-2" }
              ].map((c) => (
                <a key={c.testid} href={telHref(c.phone)} data-testid={c.testid}
                  className="flex items-center gap-3 rounded-xl border border-[#C9D4E6] bg-white px-4 py-3 hover:border-[#D4A017]/50 active:scale-[0.99] transition-all">
                  <div className="h-10 w-10 rounded-full bg-[#D4A017]/15 flex items-center justify-center shrink-0">
                    <Phone className="h-5 w-5 text-[#A87608]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[#1B2A4A] font-semibold text-sm truncate">{c.label}</p>
                    <p className="text-[#2E3B57] text-sm">{c.phone}</p>
                  </div>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#EDF1F8] text-[#2E3B57] shrink-0">{c.tag}</span>
                </a>
              ))}
              <a href={mailHref(CLAIM_DESK.email, "Claim assistance — Kavach Box")} data-testid="contact-email"
                className="flex items-center gap-3 rounded-xl border border-[#C9D4E6] bg-white px-4 py-3 hover:border-[#D4A017]/50 transition-all">
                <div className="h-10 w-10 rounded-full bg-[#EDF1F8] flex items-center justify-center shrink-0">
                  <Mail className="h-5 w-5 text-[#2E3B57]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[#1B2A4A] font-semibold text-sm">Email</p>
                  <p className="text-[#2E3B57] text-sm truncate">{CLAIM_DESK.email}</p>
                </div>
              </a>
            </div>
            <button onClick={downloadVCard} data-testid="save-contact-vcf-btn"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-[#1B2A4A] hover:bg-[#26375c] text-white font-semibold text-sm active:scale-95 transition-transform">
              <Download className="h-5 w-5" /> Save Claim Desk contact
            </button>
          </div>
        </section>

        {/* CLAIM INTIMATION FORM */}
        <section className="py-8" data-testid="claim-intimation-section">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-2">Intimate a claim</h2>
          <p className="text-[#2E3B57] text-sm mb-5">Planned or ongoing hospitalisation? Send us the basics and the Claim Desk will call you back. For emergencies, please call directly.</p>
          {submitted ? (
            <div className="glass-card p-6 text-center" data-testid="claim-intimation-success">
              <div className="h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-[#1B2A4A] mb-1.5">We've got your claim intimation ✅</h3>
              <p className="text-[#2E3B57] text-sm mb-4">Our Claim Desk will reach out shortly. If it's urgent, please call us now.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <CallBtn testid="success-call-btn" className="w-full sm:w-auto" />
                <button onClick={() => setSubmitted(false)} className="h-12 px-6 rounded-xl border border-[#C9D4E6] text-[#1B2A4A] font-semibold text-sm hover:bg-white" data-testid="intimate-another-btn">Intimate another</button>
              </div>
            </div>
          ) : (
            <form onSubmit={submitForm} className="glass-card p-5 sm:p-6 space-y-3" data-testid="claim-intimation-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <input className={inputCls} placeholder="Your name *" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="ci-name-input" />
                <input className={inputCls} placeholder="Phone number *" inputMode="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="ci-phone-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className={inputCls} placeholder="Policy number" value={form.policy_number} onChange={(e) => set("policy_number", e.target.value)} data-testid="ci-policy-input" />
                <input className={inputCls} placeholder="Insurer (e.g. Star Health)" value={form.insurer} onChange={(e) => set("insurer", e.target.value)} data-testid="ci-insurer-input" />
              </div>
              <input className={inputCls} placeholder="Hospital name" value={form.hospital_name} onChange={(e) => set("hospital_name", e.target.value)} data-testid="ci-hospital-input" />
              <textarea className={inputCls} rows={3} placeholder="Briefly describe the situation (diagnosis, planned admission date, etc.)" value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="ci-description-input" />
              <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-[#D4A017] hover:bg-[#c1930f] text-[#0A0F1A] font-bold text-sm active:scale-95 transition-transform disabled:opacity-60" data-testid="ci-submit-btn">
                {submitting ? <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</> : <><ArrowRight className="h-5 w-5" /> Intimate my claim</>}
              </button>
            </form>
          )}
        </section>

        {/* STRAIGHT TALK */}
        <section className="py-8" data-testid="straight-talk-section">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight mb-5 flex items-center gap-2"><Info className="h-5 w-5 text-[#A87608]" /> Straight talk</h2>
          <div className="glass-card p-5 sm:p-6 space-y-3">
            {STRAIGHT_TALK.map((t, i) => (
              <p key={i} className="text-[#2E3B57] text-sm sm:text-[15px] leading-relaxed flex gap-2.5" data-testid={`straight-talk-${i}`}>
                <span className="text-[#A87608] font-bold shrink-0">•</span>{t}
              </p>
            ))}
          </div>
        </section>

        {/* FINAL CTA STRIP */}
        <section className="py-8" data-testid="final-cta-section">
          <div className="rounded-2xl bg-[#1B2A4A] px-5 sm:px-8 py-8 text-center shadow-xl shadow-black/20">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-5">
              Don't wait for a claim to find out who's behind your policy.
            </h2>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <CallBtn testid="final-cta-call-btn" className="w-full sm:w-auto" />
              <WaBtn testid="final-cta-whatsapp-btn" className="w-full sm:w-auto" />
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 border-t border-[#C9D4E6] bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <a href={telHref(CLAIM_DESK.primary.phone)} className="text-[#2E3B57] hover:text-[#A87608] transition-colors flex items-center gap-1.5"><Phone className="h-4 w-4" /> {CLAIM_DESK.primary.phone}</a>
            <a href={waHref(CLAIM_DESK.whatsapp, CLAIM_DESK_WA_MESSAGE)} target="_blank" rel="noopener noreferrer" className="text-[#2E3B57] hover:text-[#A87608] transition-colors flex items-center gap-1.5"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
            <a href={mailHref(CLAIM_DESK.email)} className="text-[#2E3B57] hover:text-[#A87608] transition-colors flex items-center gap-1.5"><Mail className="h-4 w-4" /> {CLAIM_DESK.email}</a>
          </div>
          <p className="text-[#3A4557] text-xs mt-4">Kavach Box — Insurance Advisory CRM · A part of Shree Finserv Group · by Prashant K Uunadkat</p>
        </div>
      </footer>
    </div>
  );
}
